import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Logger } from "./Logger";

/**
 * Ensure the node rotates via quaternion: if it has no `rotationQuaternion`,
 * initialize one from its current Euler `rotation` (a no-visual-change
 * conversion). Needed before anything that writes or captures the quaternion
 * — Babylon animatables capture the node's original value and restore it on
 * reset, and a captured `null` crashes `Quaternion.copyFrom`. Nodes that
 * never had a quaternion stay in Euler mode untouched.
 */
export function ensureRotationQuaternion(
    node: TransformNode,
): asserts node is TransformNode & { rotationQuaternion: Quaternion } {
    if (!node.rotationQuaternion) {
        node.rotationQuaternion = Quaternion.FromEulerVector(node.rotation);
    }
}

/**
 * Transform utility functions for common transform operations
 */

/**
 * Reset a TransformNode to identity transform (position 0, rotation 0, scale 1)
 * Handles both quaternion and euler rotation modes
 *
 * @param node - The TransformNode to reset
 * @param logPrefix - Optional prefix for logging (e.g., "[Animation]")
 */
export function resetTransformToIdentity(node: TransformNode, logPrefix?: string): void {
    if (!node) return;

    node.position.setAll(0);

    if (node.rotationQuaternion) {
        node.rotationQuaternion.set(0, 0, 0, 1);
    } else if (node.rotation) {
        node.rotation.setAll(0);
    }

    node.scaling.setAll(1);

    if (logPrefix) {
        Logger.debug(`${logPrefix} Reset transform to identity for ${node.name}`);
    }
}

const _tmpPivot = new Matrix();
const _identityQuat = Quaternion.Identity();

/** Scale ratios closer to 1 than this are float noise from decomposing a
 *  uniformly scaled, rotated parent. */
const UNIFORM_SCALE_EPSILON = 1e-6;

/**
 * Pivots a turned animation root on its parent's world scale magnitudes, so
 * its local matrix is P·R·P⁻¹ and the turn lands after that scale instead of
 * under it, where a non-uniform scale shears the content. Only the ratios
 * matter, so a uniform scale, a zero scale or a root at rest keeps the
 * identity pivot. Babylon adds the root's translation outside the pivot, so
 * clip offsets are unchanged.
 */
export function pivotOnParentScale(animRoot: TransformNode, scale: Vector3): void {
    const y = Math.abs(scale.y / scale.x);
    const z = Math.abs(scale.z / scale.x);
    const uniform = Math.abs(y - 1) < UNIFORM_SCALE_EPSILON && Math.abs(z - 1) < UNIFORM_SCALE_EPSILON;
    const atRest = animRoot.rotationQuaternion?.equals(_identityQuat) ?? true;
    if (atRest || scale.x === 0 || y === 0 || z === 0 || uniform) {
        Matrix.IdentityToRef(_tmpPivot);
    } else {
        Matrix.ScalingToRef(1, y, z, _tmpPivot);
    }
    if (!animRoot.getPivotMatrix().equals(_tmpPivot)) {
        animRoot.setPivotMatrix(_tmpPivot);
    }
}
